'use client';

import { type ComponentProps, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowRight, ChevronLeft, ChevronRight, type LucideIcon as LucideIconType, Share } from 'lucide-react';
import { getListingIconType, ICON_FOR_LISTING_TYPE } from '@/src/lib/utils/listingPropertyType';
import { IconButton, PhotoCarousel, PropertyCard, Tag, VisuallyHidden } from '@zillow/constellation-10';
import { Link, useRouter } from '@/src/i18n/navigation';
import { LucideIcon } from '@/src/components/LucideIcon';
import { FavoriteHeartIcon } from '@/src/components/features/listings/FavoriteHeartIcon';
import { FederatedPhotoWithFallback as FederatedPhoto } from '@/src/components/FederatedPhoto/FederatedPhotoWithFallback';
import { FederatedPhotoPreload } from '@/src/components/FederatedPhoto';
import { MIN_LG } from '@/src/lib/breakpoints';
import type { FederatedPhotoTreatment } from '@/src/components/FederatedPhoto/federatedPhoto';
import { useListingSaveActions } from '@/src/components/features/property/hooks/useListingSaveActions';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import { useShareAction } from '@/src/lib/hooks/useShareAction';
import { TotalMonthlyPriceIndicator } from '@/src/components/features/listings/TotalMonthlyPrice';
import { useContactFormAction } from '@/src/components/features/property/ContactForm';
import { emitTrackPhoneLeadEvent } from '@/src/components/features/property/lib/analytics';
import { useListingPhotoCollection } from '@/src/lib/hooks/useListingPhotoCollection';
import { getListingHdpHref } from '@/src/lib/utils/listingHref';
import { generateMarkerId } from '@/src/lib/utils/generateMarkerId';
import { useListingFormatters } from '@/src/lib/utils/useListingFormatters';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';
import { VerifiedPhotoBadge } from '@/src/components/features/listings/VerifiedInline';
import { ListingActionMenu } from '@/src/components/features/listings/ListingActionMenu';
import { ListingActionCluster } from '@/src/components/features/listings/ListingActionCluster';
import {
  type ListingActionAnalyticsContext,
  useOptionalListingActionContext,
} from '@/src/components/features/listing-actions';
import {
  getListingCardActionContext,
  type ShareClickstreamContext,
} from '@/src/components/features/listing-actions/listingActionUtils';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackImpressionForPropertyCardEvent,
} from '@/src/analytics/hotpads/events';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';
import {
  buildingCue,
  cardContainer,
  cardForeground,
  cardLink,
  carouselDots,
  carouselNextButtonCompact,
  carouselNextButtonLarge,
  carouselPreviousButtonCompact,
  carouselPreviousButtonLarge,
  compactCardOverrides,
  dataAreaShrink,
  feesApply,
  homeDetailsArea,
  homeDetailsRow,
  homeDetailValue,
  horizontalCardOverrides,
  identityAddress,
  identityIcon,
  identityLabel,
  identityLabelGroup,
  identityRow,
  interactiveCarouselRoot,
  largeCardOverrides,
  photoImageFill,
  priceRow,
  priceValue,
  totalPriceBadge,
  totalPriceCheckIcon,
  totalPriceLabel,
} from './listingCard.styles';
import type { ListingCardProps } from './types';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { useRecentlyContactedListing } from '@/src/lib/hooks/query/useRecentlyContactedListing';
import { ListingCardContactActions } from './ListingCardContactActions';

type PhotoCarouselDotProps = ComponentProps<typeof PhotoCarousel.Dot>;

const LISTING_CARD_IMAGE_SIZES = '(min-width: 1280px) 380px, (min-width: 768px) 33vw, calc(100vw - 32px)';
/**
 * Card slots render at most ~380 CSS px wide, so cc_ft_768 covers DPR 2; the
 * unclamped srcset let high-DPR phones (e.g. 2.625x) pull cc_ft_1152 at ~2x
 * the bytes for no visible gain. Descriptors above 768w stay in the srcset so
 * browser selection is unchanged — they just resolve to capped render URLs.
 */
const LISTING_CARD_PHOTO_TREATMENT: FederatedPhotoTreatment = {
  defaultTreatment: 'cc_ft_768',
  srcSetTreatments: [
    { width: 192, treatment: 'cc_ft_192' },
    { width: 384, treatment: 'cc_ft_384' },
    { width: 576, treatment: 'cc_ft_576' },
    { width: 768, treatment: 'cc_ft_768' },
    { width: 960, treatment: 'cc_ft_768' },
    { width: 1152, treatment: 'cc_ft_768' },
    { width: 1536, treatment: 'cc_ft_960' },
  ],
  placeholderTreatment: 'cc_ft_192',
};
/**
 * Window during which a click inside the photo carousel is treated as
 * collateral from a recent arrow / dot interaction rather than a deliberate
 * "open the listing" tap. Sized to comfortably cover the trailing click
 * that fires after Constellation's slide transition settles — observed
 * occasionally past 500 ms — without making intentional image taps wait
 * unreasonably long after a single arrow press.
 */
const CAROUSEL_RAPID_INTERACTION_MS = 1000;

function uniquePhotoUrls(photos: Array<string>): Array<string> {
  const seen = new Set<string>();

  return photos.filter((photo) => {
    if (seen.has(photo)) return false;
    seen.add(photo);
    return true;
  });
}

function getContactPhone(listing: SummaryListingDetails): string | null {
  return listing.contactPhone ?? listing.listedBy?.contactPhone ?? listing.companyPhoneNumber ?? null;
}

type HdpT = ReturnType<typeof useTranslations<'hdp'>>;
type ListingT = ReturnType<typeof useTranslations<'listing'>>;

/**
 * Splits a formatted spec phrase into its numeric value and unit label so the
 * value renders bold and the label normal-weight (e.g. "3" + "beds"). The label
 * is the trailing run of alphabetic words; everything before it is the value.
 * Phrases that are all letters (e.g. "Studio") have no separate label.
 */
function splitDetail(phrase: string): { value: string; label: string } {
  const tokens = phrase.trim().split(/\s+/);
  const labelTokens: Array<string> = [];
  while (tokens.length > 1 && /^[\p{L}.]+$/u.test(tokens[tokens.length - 1] ?? '')) {
    labelTokens.unshift(tokens.pop() as string);
  }
  return { value: tokens.join(' '), label: labelTokens.join(' ') };
}

function formatAddress(listing: SummaryListingDetails): string {
  const addr = listing.address;
  if (!addr) return '';
  const parts: Array<string> = [];
  if (addr.street && !addr.hideStreet) parts.push(addr.street);
  if (addr.city) parts.push(addr.city);
  if (addr.state) parts.push(addr.state);
  return parts.join(', ');
}

function formatCityStateZip(listing: SummaryListingDetails): string {
  const addr = listing.address;
  if (!addr) return '';
  const parts: Array<string> = [];
  if (addr.city) parts.push(addr.city);
  if (addr.state) parts.push(addr.state);
  const base = parts.join(', ');
  return addr.zip ? `${base} ${addr.zip}` : base;
}

/**
 * The card's identity line: a property-type icon (with an accessible title so
 * the type is exposed on hover and to screen readers) paired with the most
 * meaningful label for that type — building name for multi-unit properties, the
 * street address for a house, etc. The type is conveyed by the icon rather than
 * a separate text label.
 */
function getListingIdentity(
  listing: SummaryListingDetails,
  address: string,
  t: HdpT,
  tListing: ListingT,
): { Icon: LucideIconType; iconTitle: string; label: string } {
  const street = listing.address?.street && !listing.address?.hideStreet ? listing.address.street : address;
  const buildingName = listing.title || address;

  const iconType = getListingIconType(listing.propertyType, listing.listingType);
  const Icon = ICON_FOR_LISTING_TYPE[iconType];
  const iconTitle =
    iconType === 'building'
      ? t('apartmentBuilding')
      : iconType === 'condo'
        ? 'Condo'
        : iconType === 'house'
          ? 'House'
          : tListing('roomForRent');

  return {
    Icon,
    iconTitle,
    label: iconType === 'house' ? street || buildingName : buildingName,
  };
}

type InteractivePhotoCarouselProps = {
  alt: string;
  canHydratePhotoCollection: boolean;
  carouselEngaged: boolean;
  clearCardFocusDuringPhotoTouchMove: (event: React.TouchEvent<HTMLElement>) => void;
  engageCarousel: () => void;
  forwardCarouselClickToCardLink: (event: React.MouseEvent) => void;
  initialPhotos: Array<string>;
  isLargeAppearance: boolean;
  listingPhotoCollectionId: string | null;
  markerId: string;
  photoCollectionRequested: boolean;
  priority: boolean;
  requestPhotoCollection: () => void;
  sizes: string;
};

/**
 * Interaction-only photo subtree. Keeping the collection hook and carousel
 * in this child means navigation-only SRP cards do not subscribe to photo
 * data or emit the carousel's controls, dots, and placeholder slides.
 */
function InteractivePhotoCarousel({
  alt,
  canHydratePhotoCollection,
  carouselEngaged,
  clearCardFocusDuringPhotoTouchMove,
  engageCarousel,
  forwardCarouselClickToCardLink,
  initialPhotos,
  isLargeAppearance,
  listingPhotoCollectionId,
  markerId,
  photoCollectionRequested,
  priority,
  requestPhotoCollection,
  sizes,
}: InteractivePhotoCarouselProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const { data: photoCollection } = useListingPhotoCollection(listingPhotoCollectionId, {
    enabled: photoCollectionRequested && canHydratePhotoCollection,
  });
  const photos = uniquePhotoUrls(photoCollection?.medPhotoUrls.length ? photoCollection.medPhotoUrls : initialPhotos);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const carouselButtonPressRef = useRef(false);
  const lastCarouselButtonActivityRef = useRef(0);

  const handleCarouselPointerDown = useCallback(
    (event: React.PointerEvent) => {
      engageCarousel();
      requestPhotoCollection();
      const isButton = Boolean((event.target as HTMLElement).closest('button'));
      carouselButtonPressRef.current = isButton;
      if (isButton) lastCarouselButtonActivityRef.current = Date.now();
    },
    [engageCarousel, requestPhotoCollection],
  );

  const stopCarouselNavigation = useCallback(
    (event: React.MouseEvent) => {
      engageCarousel();
      const wasButtonPress = carouselButtonPressRef.current || Boolean((event.target as HTMLElement).closest('button'));
      carouselButtonPressRef.current = false;
      const now = Date.now();
      const inRapidInteractionWindow = now - lastCarouselButtonActivityRef.current < CAROUSEL_RAPID_INTERACTION_MS;

      if (wasButtonPress || inRapidInteractionWindow) {
        lastCarouselButtonActivityRef.current = now;
        event.preventDefault();
      }
    },
    [engageCarousel],
  );

  const photoSlides = photos.map((url, index) =>
    index === 0 || carouselEngaged ? (
      <FederatedPhoto
        key={`${url}-${index}`}
        src={url}
        alt={alt}
        sizes={sizes}
        treatment={LISTING_CARD_PHOTO_TREATMENT}
        priority={priority && index === 0}
        fit="cover"
        placeholder="tone"
        className={photoImageFill}
      />
    ) : (
      <div key={`${url}-${index}`} className={photoImageFill} />
    ),
  );

  if (photos.length === 0) return <PropertyCard.PhotoFallback altText={alt} />;

  return (
    <PhotoCarousel.Root
      key={markerId}
      aria-label={alt}
      css={interactiveCarouselRoot}
      shouldLoop={isHydrated && photos.length > 2}
      onPointerDownCapture={handleCarouselPointerDown}
      onClickCapture={stopCarouselNavigation}
      onClick={forwardCarouselClickToCardLink}
      onTouchMove={clearCardFocusDuringPhotoTouchMove}
    >
      {/* Constellation's Dots paints its scrim capsule even with zero dots
          (totalSlides is 0 until Slides' client effect runs, and stays under 2
          for single-photo cards), so gate the mount and pass the count for SSR. */}
      {photos.length > 1 && (
        <PhotoCarousel.Dots
          totalSlides={photos.length}
          maxDotsToDisplay={isLargeAppearance ? 6 : 5}
          css={carouselDots}
          renderDot={({ key, dotProps }) => (
            <PhotoCarousel.Dot
              key={key}
              {...(dotProps as PhotoCarouselDotProps)}
              data-testid="listing-card-photo-dot"
            />
          )}
        />
      )}
      <PhotoCarousel.NavControls>
        <PhotoCarousel.PreviousButton
          data-testid="button-previous-photo"
          css={isLargeAppearance ? carouselPreviousButtonLarge : carouselPreviousButtonCompact}
        >
          <LucideIcon icon={ChevronLeft} size="sm" />
        </PhotoCarousel.PreviousButton>
        <PhotoCarousel.NextButton
          data-testid="button-next-photo"
          css={isLargeAppearance ? carouselNextButtonLarge : carouselNextButtonCompact}
        >
          <LucideIcon icon={ChevronRight} size="sm" />
        </PhotoCarousel.NextButton>
      </PhotoCarousel.NavControls>
      <PhotoCarousel.Slides>{photoSlides}</PhotoCarousel.Slides>
    </PhotoCarousel.Root>
  );
}

type InteractiveListingCardActionsProps = {
  children: (actions: {
    actionButton: React.ReactNode;
    flexArea?: React.ReactNode;
    saveButton: React.ReactNode;
  }) => React.ReactNode;
  isLargeAppearance: boolean;
  listing: ListingCardProps['listing'];
  listingActionAnalyticsContext: ListingActionAnalyticsContext;
  saveClickstreamContext: NonNullable<ListingCardProps['saveClickstreamContext']>;
  shareClickstreamContext: ShareClickstreamContext;
  showContactActions: boolean;
  stopCardNavigation: (event: React.MouseEvent) => void;
};

/** Mount save/share/menu/contact subscriptions only after a card becomes interactive. */
function InteractiveListingCardActions({
  children,
  isLargeAppearance,
  listing,
  listingActionAnalyticsContext,
  saveClickstreamContext,
  shareClickstreamContext,
  showContactActions,
  stopCardNavigation,
}: InteractiveListingCardActionsProps) {
  const t = useTranslations('hdp');
  const tListing = useTranslations('listing');
  const favoriteButtonRef = useRef<HTMLButtonElement>(null);
  const { listingSaved, savePending, toggleSave } = useListingSaveActions(
    listing,
    favoriteButtonRef,
    saveClickstreamContext,
  );
  const contactTriggerLocation =
    listingActionAnalyticsContext.triggerLocation === 'user_item_list_page' ? 'user_item_list_page' : 'search_results';
  const { openShare, shareFormOverlay } = useShareAction(listing as ExtendedListing, shareClickstreamContext);
  const {
    openContactForm,
    contactFormOverlay,
    identityResolved: contactIdentityResolved,
  } = useContactFormAction(listing as ExtendedListing, contactTriggerLocation, 'property_card');
  const listingActionContext = useOptionalListingActionContext();
  const listingHidden = listingActionContext?.isListingHidden(listing) ?? false;
  const { data: currentUser } = useCurrentUser();
  const recentlyContacted = useRecentlyContactedListing(listing, {
    loggedIn: currentUser?.loggedIn === true,
    email: currentUser?.data?.email,
  });
  const contactPhone = getContactPhone(listing);
  const shouldShowContactActions = showContactActions && isLargeAppearance && listing.active !== false;

  const stopCardClickPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleCall = useCallback(() => {
    emitTrackPhoneLeadEvent({
      listing: toHotpadsClickstreamListing(listing),
      triggerLocation: contactTriggerLocation,
      triggerObject: 'property_card',
    });
  }, [contactTriggerLocation, listing]);

  const handleHide = useCallback(
    (event: React.MouseEvent) => {
      stopCardNavigation(event);
      listingActionContext?.toggleHideListing(listing, listingActionAnalyticsContext);
    },
    [listing, listingActionAnalyticsContext, listingActionContext, stopCardNavigation],
  );

  const handleReport = useCallback(
    (event: React.MouseEvent) => {
      stopCardNavigation(event);
      listingActionContext?.openReport(listing, listingActionAnalyticsContext);
    },
    [listing, listingActionAnalyticsContext, listingActionContext, stopCardNavigation],
  );

  const handleShare = useCallback(
    (event: React.MouseEvent) => {
      stopCardNavigation(event);
      openShare();
    },
    [openShare, stopCardNavigation],
  );

  const favoriteSelected = listingSaved || savePending;
  const favoriteButton = (
    <IconButton
      ref={favoriteButtonRef}
      shape="circle"
      title={favoriteSelected ? tListing('removeFromFavorites') : tListing('addToFavorites')}
      aria-label={favoriteSelected ? tListing('removeFromFavorites') : tListing('addToFavorites')}
      aria-pressed={favoriteSelected}
      aria-busy={savePending || undefined}
      aria-disabled={savePending || undefined}
      onClick={(event) => {
        stopCardNavigation(event);
        if (savePending) return;
        toggleSave();
      }}
      data-testid="listing-card-favorite"
      icon={<FavoriteHeartIcon selected={favoriteSelected} />}
    />
  );

  const shareButton = (
    <IconButton
      shape="circle"
      title={t('share')}
      aria-label={t('share')}
      onClick={handleShare}
      data-testid="listing-card-share"
    >
      <LucideIcon icon={Share} />
    </IconButton>
  );

  const saveButton = (
    <ListingActionCluster label={t('listingOptions')}>
      {favoriteButton}
      {shareButton}
    </ListingActionCluster>
  );

  const actionButton = (
    <ListingActionMenu
      listingHidden={listingHidden}
      onHide={handleHide}
      onReport={handleReport}
      onTriggerClick={stopCardClickPropagation}
    />
  );

  const flexArea = shouldShowContactActions ? (
    <ListingCardContactActions
      recentlyContacted={recentlyContacted}
      contactPhone={contactPhone}
      messageDisabled={!contactIdentityResolved}
      onMessage={openContactForm}
      onCall={handleCall}
    />
  ) : undefined;

  return (
    <>
      {children({ actionButton, flexArea, saveButton })}
      {shareFormOverlay}
      {contactFormOverlay}
    </>
  );
}

/**
 * Unified listing card built on Constellation's high-level `<PropertyCard>`
 * API (the structured-props form — saveButton/actionButton/photoBody/photoFooter/
 * badge/data slots — not the lower-level compound primitives, which can't be
 * composed under `asChild`).
 *
 * The `data` object maps onto PropertyCard's canonical slots — price is the
 * prominent title (`dataArea1`) and beds/baths/sqft go through
 * `PropertyCard.HomeDetails` (`dataArea2`). The identity line (`dataArea3`)
 * pairs a property-type icon with the building name / street address plus the
 * multi-unit "View units" cue. `dataArea4` is intentionally unused: the large
 * appearance appends it onto the home-details line (and clamps it), so the
 * property type is conveyed by the dataArea3 icon instead. Promo signals
 * surface as a single priority `PropertyCard.Badge` over the photo.
 * `appearance` forwards straight to Constellation, which lays each variant
 * (large / small / horizontal) out itself.
 */
export const ListingCard = memo(function ListingCard({
  listing,
  href: hrefProp,
  priority = false,
  desktopPriority = false,
  prefetch,
  sizes = LISTING_CARD_IMAGE_SIZES,
  hrefOverride,
  onNavigate,
  children,
  appearance = 'large',
  showContactActions = false,
  readOnly = false,
  interactionMode = 'full',
  hideListingActions = false,
  saveClickstreamContext,
  shareClickstreamContext,
  actionClickstreamContext,
  clickstreamTriggerLocation,
}: ListingCardProps) {
  const router = useRouter();
  const t = useTranslations('hdp');
  const tListing = useTranslations('listing');
  const { formatPrice, formatBedrooms, formatBathrooms, formatSqft } = useListingFormatters();
  const resolvedClickstreamTriggerLocation = clickstreamTriggerLocation ?? 'search_results_list';
  const listingActionAnalyticsContext =
    actionClickstreamContext ?? getListingCardActionContext(resolvedClickstreamTriggerLocation);
  const resolvedSaveContext = saveClickstreamContext ?? {
    ...listingActionAnalyticsContext,
    triggerObject:
      clickstreamTriggerLocation === 'portfolio_listings_module' || clickstreamTriggerLocation === 'building_unit_card'
        ? ('home_details_component|more_from_provider' as const)
        : listingActionAnalyticsContext.triggerObject,
  };

  const href = hrefProp ?? hrefOverride ?? getListingHdpHref(listing) ?? `/listings/${listing.aliasEncoded}`;
  const initialPhotos = listing.medPhotoUrls?.length
    ? listing.medPhotoUrls
    : listing.medPhotoUrl
      ? [listing.medPhotoUrl]
      : [];
  const listingPhotoCollectionId =
    listing.maloneLotIdEncoded ?? (listing as { lotIdEncoded?: string }).lotIdEncoded ?? null;
  const canHydratePhotoCollection = Boolean(listingPhotoCollectionId && initialPhotos.length <= 1);
  const [photoCollectionRequested, setPhotoCollectionRequested] = useState(false);
  /**
   * Carousel slides sit at translateX(±100%) — never display:none — so any
   * lazy <img> mounted in the offscreen prev/next slides is within the
   * browser's lazy-load fetch distance and downloads on page load (~3 photos
   * per card across a 40-card SRP). Alternate photos therefore stay unmounted
   * until the user signals intent on the card or its carousel (HP-8667).
   *
   * Engagement is keyed to the listing's stable identity (generateMarkerId,
   * the same id consumers use for React keys), not a plain boolean or the
   * href: a mounted card can be handed a different listing without a remount
   * (the map info-card popup swapping markers), which must disengage the
   * carousel, while SRP hrefs carry mutable filter/viewport carryover params
   * that change without a listing swap and must not disengage it.
   */
  const markerId = generateMarkerId(listing as ExtendedListing);
  const [engagedListingId, setEngagedListingId] = useState<string | null>(null);
  const carouselEngaged = engagedListingId === markerId;
  const engageCarousel = useCallback(() => setEngagedListingId(markerId), [markerId]);
  const requestPhotoCollection = useCallback(() => {
    if (canHydratePhotoCollection) setPhotoCollectionRequested(true);
  }, [canHydratePhotoCollection]);
  const alt = listing.title || listing.address?.street || t('listingPhoto');

  const price = formatPrice(listing);
  const hasDollarPrice = price.startsWith('$');
  const priceDisplay = hasDollarPrice ? `${price}${price.endsWith('+') ? ' ' : ''}${t('perMoSlash')}` : price;
  const bedDisplay = formatBedrooms(listing);
  const bathDisplay = formatBathrooms(listing);
  const sqftDisplay = formatSqft(listing);
  const address = formatAddress(listing);
  const cityStateZip = formatCityStateZip(listing);
  const displayTag = listing.displayTags?.[0];
  const unitCount = listing.modelTypeUnitCount ?? 0;
  // Treat owner-claimed and paid-advertiser listings as "verified" for the
  // shield-check footer affordance. Refined `isVerified` field can replace
  // this proxy when the API surfaces one.
  const isVerified = Boolean(listing.isClaimedFreeListing || listing.isPaidAndNotDemoted);
  const isLargeAppearance = appearance === 'large';
  const isCompactAppearance = appearance === 'small';
  const showsPhotoOverlays = !readOnly && appearance !== 'horizontal';
  const hasFullInteraction = !readOnly && interactionMode === 'full';
  // Hidden actions need no save/share/contact subscriptions, so skip the wrapper.
  const mountsListingActions = hasFullInteraction && !hideListingActions;
  const opensBuildingPage = listing.opensBuildingPage === true;
  const identity = getListingIdentity(listing, address, t, tListing);
  const buildingPageCueLabel = opensBuildingPage ? tListing('viewUnits') : null;

  // beds / baths / sqft (or unit count for buildings) fed to Constellation's
  // HomeDetails. Each phrase is split so the value renders bold and the unit
  // label normal-weight (the "|" separator is suppressed in the styles).
  const homeDetails = [
    bedDisplay,
    unitCount > 0 ? t('unitsAvailableSep', { count: unitCount }) : bathDisplay,
    unitCount > 0 ? null : sqftDisplay,
  ]
    .filter((phrase): phrase is string => Boolean(phrase))
    .map(splitDetail);

  const cardRef = useRef<HTMLAnchorElement | null>(null);
  const trackedImpressionListingIdRef = useRef<string | null>(null);
  const navigationScrollYRef = useRef<number | null>(null);
  const prefetchedListingCardHrefs = useRef(new Set<string>());

  useEffect(() => {
    if (readOnly || trackedImpressionListingIdRef.current === markerId) return;

    const element = cardRef.current;
    if (!element) return;

    const trackImpression = () => {
      if (trackedImpressionListingIdRef.current === markerId) return;
      trackedImpressionListingIdRef.current = markerId;
      emitTrackImpressionForPropertyCardEvent({
        listing: toHotpadsClickstreamListing(listing),
        triggerLocation: resolvedClickstreamTriggerLocation,
      });
    };

    if (typeof IntersectionObserver === 'undefined') {
      trackImpression();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      trackImpression();
      observer.disconnect();
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [listing, markerId, readOnly, resolvedClickstreamTriggerLocation]);

  const linkPrefetch = onNavigate ? false : prefetch;
  const prefetchListingRoute = useCallback(() => {
    if (linkPrefetch !== false || prefetchedListingCardHrefs.current.has(href)) return;

    prefetchedListingCardHrefs.current.add(href);
    router.prefetch(href as Parameters<typeof router.prefetch>[0]);
  }, [href, linkPrefetch, router]);

  const handleCardIntent = useCallback(() => {
    engageCarousel();
    requestPhotoCollection();
    prefetchListingRoute();
  }, [engageCarousel, prefetchListingRoute, requestPhotoCollection]);

  const runNavigationSideEffects = useCallback(
    (searchScrollY: number | null) => {
      if (!onNavigate) return;

      if (searchScrollY == null) {
        onNavigate(listing);
        return;
      }

      onNavigate(listing, { searchScrollY });
    },
    [listing, onNavigate],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const interactiveElement = (e.target as HTMLElement).closest('a, button, [role="button"]');
      if (interactiveElement && interactiveElement !== e.currentTarget) return;
      // A deeper handler (the carousel's discrimination logic, the save /
      // menu buttons, etc.) preventDefault'd to opt out of card navigation.
      // Honor that intent rather than invoking the panel-push handler too.
      if (e.defaultPrevented) return;
      if (e.button !== 0 && e.button !== 1) return;
      emitTrackClicksIntoPropertyEvent({
        listing: toHotpadsClickstreamListing(listing),
        triggerLocation: resolvedClickstreamTriggerLocation,
      });
      if (!onNavigate) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const searchScrollY = navigationScrollYRef.current;
      navigationScrollYRef.current = null;
      runNavigationSideEffects(searchScrollY);
    },
    [listing, onNavigate, resolvedClickstreamTriggerLocation, runNavigationSideEffects],
  );

  const captureNavigationScroll = useCallback(() => {
    navigationScrollYRef.current = window.scrollY || window.pageYOffset || 0;
  }, []);

  const stopCardNavigation = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const clearCardFocusDuringPhotoTouchMove = useCallback((_event: React.TouchEvent<HTMLElement>) => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement === cardRef.current) {
      activeElement.blur();
    }
  }, []);

  const forwardCarouselClickToCardLink = useCallback((event: React.MouseEvent) => {
    if (event.defaultPrevented || !cardRef.current) return;

    event.stopPropagation();
    // Relay through the real link so Next.js and modified-click navigation semantics stay intact.
    cardRef.current.dispatchEvent(
      new MouseEvent('click', {
        altKey: event.altKey,
        bubbles: true,
        button: event.button,
        buttons: event.buttons,
        cancelable: true,
        clientX: event.clientX,
        clientY: event.clientY,
        composed: true,
        ctrlKey: event.ctrlKey,
        detail: event.detail,
        metaKey: event.metaKey,
        screenX: event.screenX,
        screenY: event.screenY,
        shiftKey: event.shiftKey,
      }),
    );
  }, []);

  const totalPriceIndicator = (
    <TotalMonthlyPriceIndicator
      listing={listing}
      testId="listing-card-total-price"
      feesTestId="listing-card-fees"
      onTriggerClick={stopCardNavigation}
      classNames={{
        badge: totalPriceBadge,
        fees: feesApply,
        icon: totalPriceCheckIcon,
        label: totalPriceLabel,
      }}
    />
  );

  const cardData = {
    // Price is the prominent title line, with the total-price / fees indicator
    // inline beside it. `lineClamp: 'none'` keeps the indicator from being clipped.
    dataArea1: {
      lineClamp: 'none' as const,
      css: dataAreaShrink,
      children: (
        <span className={priceRow}>
          <span className={priceValue}>{priceDisplay}</span>
          {hasDollarPrice && totalPriceIndicator}
        </span>
      ),
    },
    // Beds/baths/sqft on a single line. `lineClamp: 'none'` + nowrap/ellipsis
    // keeps it to one line that extends to the full card width before
    // truncating — Constellation's default `-webkit-line-clamp` clamps it far
    // too early. (Also: in the large appearance Constellation appends
    // `dataArea4` onto this line, so the property type lives in dataArea3 as an
    // icon instead — see below.)
    dataArea2:
      homeDetails.length > 0
        ? {
            lineClamp: 'none' as const,
            css: homeDetailsRow,
            children: (
              <span className={homeDetailsArea} data-testid="listing-card-home-details">
                {homeDetails.map((detail, i) => (
                  <span key={i}>
                    {i > 0 && ' · '}
                    <span className={homeDetailValue}>{detail.value}</span>
                    {detail.label && ` ${detail.label}`}
                  </span>
                ))}
              </span>
            ),
          }
        : undefined,
    // Identity line: property-type icon + building name / street address, plus
    // the multi-unit "View units" cue when the card opens a building page.
    dataArea3: {
      lineClamp: 'none' as const,
      css: dataAreaShrink,
      children: (
        <span className={identityRow}>
          <span className={identityLabelGroup}>
            <LucideIcon icon={identity.Icon} size="sm" className={identityIcon} title={identity.iconTitle} />
            {identity.label && <span className={identityLabel}>{identity.label}</span>}
          </span>
          {cityStateZip && <span className={identityAddress}>{cityStateZip}</span>}
          {buildingPageCueLabel && (
            <span className={buildingCue} data-testid="listing-card-building-cue">
              {buildingPageCueLabel}
              <LucideIcon icon={ArrowRight} size="sm" />
            </span>
          )}
        </span>
      ),
    },
  };

  // Above the fold only on lg+ (HP-8668): fetchpriority=high or eager on the
  // <img> itself would compete with the mobile LCP image on every viewport,
  // so the parser-time high-priority hint is scoped to lg+ via a media
  // preload and the <img> markup stays untouched. `priority` cards already
  // preload on both breakpoints through next/image.
  const desktopPriorityPreload =
    desktopPriority && !priority && !readOnly && initialPhotos.length > 0 ? (
      <FederatedPhotoPreload
        src={initialPhotos[0]}
        sizes={sizes}
        treatment={LISTING_CARD_PHOTO_TREATMENT}
        media={MIN_LG}
      />
    ) : null;

  const photoCarousel = hasFullInteraction ? (
    <InteractivePhotoCarousel
      alt={alt}
      canHydratePhotoCollection={canHydratePhotoCollection}
      carouselEngaged={carouselEngaged}
      clearCardFocusDuringPhotoTouchMove={clearCardFocusDuringPhotoTouchMove}
      engageCarousel={engageCarousel}
      forwardCarouselClickToCardLink={forwardCarouselClickToCardLink}
      initialPhotos={initialPhotos}
      isLargeAppearance={isLargeAppearance}
      listingPhotoCollectionId={listingPhotoCollectionId}
      markerId={markerId}
      photoCollectionRequested={photoCollectionRequested}
      priority={priority}
      requestPhotoCollection={requestPhotoCollection}
      sizes={sizes}
    />
  ) : null;

  // Read-only preview: a single static photo (no carousel), no favorite/action
  // controls — robust in tight surfaces like the Share modal where the carousel
  // and interactive chrome misbehave.
  const staticPhoto =
    initialPhotos.length > 0 ? (
      <FederatedPhoto
        src={initialPhotos[0]}
        alt={alt}
        sizes={sizes}
        treatment={LISTING_CARD_PHOTO_TREATMENT}
        priority={priority}
        fit="cover"
        placeholder="tone"
        className={photoImageFill}
      />
    ) : (
      <PropertyCard.PhotoFallback altText={alt} />
    );

  const renderPropertyCard = ({
    actionButton,
    flexArea,
    saveButton,
  }: { actionButton?: React.ReactNode; flexArea?: React.ReactNode; saveButton?: React.ReactNode } = {}) => (
    <PropertyCard
      appearance={appearance}
      elevated={false}
      // Constellation defaults `interactive` to true (cursor:pointer + hover).
      // The read-only preview isn't clickable, so turn it off there.
      interactive={!readOnly}
      css={
        isLargeAppearance ? largeCardOverrides : isCompactAppearance ? compactCardOverrides : horizontalCardOverrides
      }
      badge={
        !showsPhotoOverlays || !displayTag ? undefined : (
          <Tag tone={displayTag.tone} size="sm" data-testid="listing-tag">
            {displayTag.text}
          </Tag>
        )
      }
      saveButton={saveButton}
      actionButton={actionButton}
      photoBody={
        hasFullInteraction ? (
          photoCarousel
        ) : initialPhotos.length > 0 ? (
          staticPhoto
        ) : (
          <PropertyCard.PhotoFallback altText={alt} />
        )
      }
      photoFooter={
        showsPhotoOverlays && isVerified ? (
          <VerifiedPhotoBadge onLabelClick={stopCardNavigation} data-testid="listing-card-verified" />
        ) : undefined
      }
      data={cardData}
      flexArea={flexArea}
    />
  );
  const propertyCard = mountsListingActions ? (
    <InteractiveListingCardActions
      isLargeAppearance={isLargeAppearance}
      listing={listing}
      listingActionAnalyticsContext={listingActionAnalyticsContext}
      saveClickstreamContext={resolvedSaveContext}
      shareClickstreamContext={shareClickstreamContext ?? listingActionAnalyticsContext}
      showContactActions={showContactActions}
      stopCardNavigation={stopCardNavigation}
    >
      {({ actionButton, flexArea, saveButton }) =>
        renderPropertyCard({
          actionButton: showsPhotoOverlays ? actionButton : undefined,
          flexArea,
          saveButton: showsPhotoOverlays ? saveButton : undefined,
        })
      }
    </InteractiveListingCardActions>
  ) : (
    renderPropertyCard()
  );

  if (readOnly) {
    return (
      <div data-testid="listing-card">
        {propertyCard}
        {children}
      </div>
    );
  }

  return (
    <div
      className={cardContainer}
      onFocus={handleCardIntent}
      onPointerEnter={handleCardIntent}
      onPointerDownCapture={() => {
        handleCardIntent();
        captureNavigationScroll();
      }}
      onClick={handleClick}
      onAuxClick={handleClick}
    >
      {desktopPriorityPreload}
      <Link
        href={href}
        prefetch={linkPrefetch}
        data-testid="listing-card"
        className={cardLink}
        onClick={handleClick}
        onAuxClick={handleClick}
        ref={cardRef}
      >
        <VisuallyHidden>{cityStateZip ? `${alt}, ${cityStateZip}` : alt}</VisuallyHidden>
      </Link>
      {propertyCard}
      <div className={cardForeground}>{children}</div>
    </div>
  );
});
